import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  Bell, MessageSquare, Phone, Mail, AlertTriangle, Shield,
  DollarSign, Clock, Zap, Check, Plus, Trash2, Settings,
  Smartphone, Send, TrendingUp, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillShockPrevention = () => {
  const [alerts, setAlerts] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [settings, setSettings] = useState({
    smsEnabled: true,
    emailEnabled: true,
    whatsappEnabled: false,
    phoneNumber: '',
    email: '',
    quietHoursEnabled: false,
    quietStart: '22:00',
    quietEnd: '08:00'
  });
  const [newAlert, setNewAlert] = useState({
    name: '',
    threshold: 1000,
    type: 'hourly',
    channel: 'sms'
  });
  const [loading, setLoading] = useState(true);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const { shouldShowData } = useDemoData();

  useEffect(() => {
    fetchAlertData();
  }, []);

  const fetchAlertData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bill-shock/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
        setRecentAlerts(data.recent_alerts || []);
        setSettings(prev => ({ ...prev, ...data.settings }));
      } else if (shouldShowData) {
        setDemoData();
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      if (shouldShowData) {
        setDemoData();
      }
    } finally {
      setLoading(false);
    }
  };

  const setDemoData = () => {
    setAlerts([
      { id: 1, name: 'Hourly Spend Spike', threshold: 500, type: 'hourly', channel: 'sms', active: true, triggered: 3 },
      { id: 2, name: 'Daily Budget Warning', threshold: 5000, type: 'daily', channel: 'email', active: true, triggered: 12 },
      { id: 3, name: 'Snowflake Query Cost', threshold: 100, type: 'per_query', channel: 'whatsapp', active: false, triggered: 0 },
      { id: 4, name: 'Weekend Anomaly', threshold: 1000, type: 'anomaly', channel: 'sms', active: true, triggered: 5 }
    ]);
    setRecentAlerts([
      { id: 1, name: 'Hourly Spend Spike', message: 'Your hourly spend hit $623 (threshold: $500)', time: '5 minutes ago', severity: 'high' },
      { id: 2, name: 'Daily Budget Warning', message: 'You\'ve used 80% of your daily budget', time: '2 hours ago', severity: 'medium' },
      { id: 3, name: 'Weekend Anomaly', message: 'Unusual activity detected on Saturday', time: '1 day ago', severity: 'low' }
    ]);
    setSettings({
      smsEnabled: true,
      emailEnabled: true,
      whatsappEnabled: false,
      phoneNumber: '+1 (555) 123-4567',
      email: 'admin@company.com',
      quietHoursEnabled: true,
      quietStart: '22:00',
      quietEnd: '08:00'
    });
  };

  const addAlert = () => {
    if (!newAlert.name || !newAlert.threshold) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const alert = {
      id: Date.now(),
      ...newAlert,
      active: true,
      triggered: 0
    };
    
    setAlerts(prev => [...prev, alert]);
    setNewAlert({ name: '', threshold: 1000, type: 'hourly', channel: 'sms' });
    setShowAddAlert(false);
    toast.success('Alert created successfully!');
  };

  const toggleAlert = (id) => {
    setAlerts(prev => prev.map(a => 
      a.id === id ? { ...a, active: !a.active } : a
    ));
  };

  const deleteAlert = (id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
    toast.success('Alert deleted');
  };

  const testAlert = async () => {
    toast.success('Test alert sent! Check your phone/email.');
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'sms': return <Smartphone className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'whatsapp': return <MessageSquare className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Bell className="w-12 h-12 text-emerald-500 animate-bounce mx-auto mb-4" />
          <p className="text-slate-400">Loading Bill Shock Prevention...</p>
        </div>
      </div>
    );
  }

  if (!shouldShowData) {
    return (
      <div className="space-y-6" data-testid="bill-shock-prevention">
        <EmptyDataState title="Bill Shock Prevention" description="Connect your cloud providers to set up cost alerts and prevent unexpected billing surprises." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bill-shock-prevention">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="h-7 w-7 text-emerald-500" />
            Bill Shock Prevention
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Instant SMS, Email & WhatsApp alerts before costs spiral out of control
          </p>
        </div>
        <Button onClick={testAlert} variant="outline" className="gap-2">
          <Send className="w-4 h-4" />
          Test Alert
        </Button>
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-emerald-500 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Active Alerts</p>
                <p className="text-3xl font-bold">{alerts.filter(a => a.active).length}</p>
              </div>
              <Bell className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-500 bg-gradient-to-br from-amber-500 to-orange-500 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Triggered This Month</p>
                <p className="text-3xl font-bold">{alerts.reduce((sum, a) => sum + a.triggered, 0)}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-500 bg-gradient-to-br from-blue-500 to-indigo-500 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Avg Response Time</p>
                <p className="text-3xl font-bold">&lt;30s</p>
              </div>
              <Clock className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-emerald-500 bg-gradient-to-br from-emerald-500 to-pink-500 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-sm">Savings Protected</p>
                <p className="text-3xl font-bold">$47K</p>
              </div>
              <DollarSign className="w-8 h-8 text-cyan-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-red-500" />
            Recent Alerts
          </CardTitle>
          <CardDescription>Latest notifications sent to your devices</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentAlerts.map((alert) => (
              <div key={alert.id} className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                      alert.severity === 'high' ? 'text-red-500' :
                      alert.severity === 'medium' ? 'text-amber-500' : 'text-emerald-500'
                    }`} />
                    <div>
                      <p className="font-medium">{alert.name}</p>
                      <p className="text-sm opacity-80">{alert.message}</p>
                    </div>
                  </div>
                  <span className="text-xs opacity-60">{alert.time}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Alert Rules */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Alert Rules</CardTitle>
                <CardDescription>Configure when you want to be notified</CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowAddAlert(true)} className="gap-1">
                <Plus className="w-4 h-4" />
                Add Rule
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      alert.active ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-400'
                    }`}>
                      {getChannelIcon(alert.channel)}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{alert.name}</p>
                      <p className="text-sm text-slate-400">
                        ${alert.threshold.toLocaleString()} / {alert.type} • {alert.triggered} triggered
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={alert.active} 
                      onCheckedChange={() => toggleAlert(alert.id)}
                    />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => deleteAlert(alert.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Alert Form */}
            {showAddAlert && (
              <div className="mt-4 p-4 rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20">
                <h4 className="font-medium text-emerald-800 dark:text-emerald-200 mb-3">New Alert Rule</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Input 
                    placeholder="Alert name"
                    value={newAlert.name}
                    onChange={(e) => setNewAlert(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <Input 
                    type="number"
                    placeholder="Threshold ($)"
                    value={newAlert.threshold}
                    onChange={(e) => setNewAlert(prev => ({ ...prev, threshold: parseInt(e.target.value) }))}
                  />
                  <select 
                    className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                    value={newAlert.type}
                    onChange={(e) => setNewAlert(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="per_query">Per Query</option>
                    <option value="anomaly">Anomaly</option>
                  </select>
                  <select 
                    className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                    value={newAlert.channel}
                    onChange={(e) => setNewAlert(prev => ({ ...prev, channel: e.target.value }))}
                  >
                    <option value="sms">SMS</option>
                    <option value="email">Email</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={addAlert} className="bg-emerald-600 hover:bg-emerald-700">
                    <Check className="w-4 h-4 mr-1" />
                    Create
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddAlert(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Notification Settings
            </CardTitle>
            <CardDescription>Configure how you receive alerts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* SMS */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">SMS Alerts</p>
                  <p className="text-sm text-slate-400">{settings.phoneNumber || 'Not configured'}</p>
                </div>
              </div>
              <Switch checked={settings.smsEnabled} onCheckedChange={(v) => setSettings(prev => ({ ...prev, smsEnabled: v }))} />
            </div>

            {/* Email */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">Email Alerts</p>
                  <p className="text-sm text-slate-400">{settings.email || 'Not configured'}</p>
                </div>
              </div>
              <Switch checked={settings.emailEnabled} onCheckedChange={(v) => setSettings(prev => ({ ...prev, emailEnabled: v }))} />
            </div>

            {/* WhatsApp */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">WhatsApp Alerts</p>
                  <p className="text-sm text-slate-400">
                    {settings.whatsappEnabled ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              <Switch checked={settings.whatsappEnabled} onCheckedChange={(v) => setSettings(prev => ({ ...prev, whatsappEnabled: v }))} />
            </div>

            {/* Quiet Hours */}
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <p className="font-medium">Quiet Hours</p>
                </div>
                <Switch 
                  checked={settings.quietHoursEnabled} 
                  onCheckedChange={(v) => setSettings(prev => ({ ...prev, quietHoursEnabled: v }))} 
                />
              </div>
              {settings.quietHoursEnabled && (
                <p className="text-sm text-slate-400">
                  No alerts between {settings.quietStart} - {settings.quietEnd} (except critical)
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="bg-slate-900 bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            How Bill Shock Prevention Works
          </h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center mx-auto mb-2">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <p className="font-medium">Real-time Monitoring</p>
              <p className="text-sm text-slate-400">We track your costs every minute</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center mx-auto mb-2">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <p className="font-medium">Threshold Detection</p>
              <p className="text-sm text-slate-400">AI detects when you're about to exceed</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center mx-auto mb-2">
                <Send className="w-6 h-6 text-blue-400" />
              </div>
              <p className="font-medium">Instant Alert</p>
              <p className="text-sm text-slate-400">Get notified in under 30 seconds</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center mx-auto mb-2">
                <Shield className="w-6 h-6 text-cyan-400" />
              </div>
              <p className="font-medium">Take Action</p>
              <p className="text-sm text-slate-400">Stop runaway costs before damage</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillShockPrevention;
